import React, { useState, useMemo } from 'react';
import type { CSSProperties } from 'react';
import { useTranslation } from 'react-i18next';

const PRO_ANNUAL_PRICE_EUR = 3.90;
const PRO_ANNUAL_PRICE_CENTS = Math.round(PRO_ANNUAL_PRICE_EUR * 100);
const CURRENCY_SYMBOL = '€';

export interface PaymentRecord {
  id: string;
  studentId: string;
  studentName: string;
  studentClass?: string;
  assignedBusNumber?: string | number;
  period: string;
  amount: number;
  status: 'paid' | 'pending';
  isPro?: boolean;
  parentEmail?: string;
  parentDocId?: string;
}

interface PaymentSheetProps {
  parents?: any[];
  students?: any[];
  onCheckout?: (payments: PaymentRecord[], totalAmountInCents: number) => void;
  onDownloadReceipt?: (payment: PaymentRecord) => void;
}

export const PaymentSheet: React.FC<PaymentSheetProps> = ({
  parents = [],
  students = [],
  onCheckout,
  onDownloadReceipt,
}) => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';

  const currentPeriod = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth();
    return month >= 8 ? `${year}-${year + 1}` : `${year - 1}-${year}`;
  }, []);

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterClass, setFilterClass] = useState('all');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showConfirmModal, setShowConfirmModal] = useState(false);

  const combinedPayments = useMemo(() => {
    const list: PaymentRecord[] = [];

    if (parents.length > 0) {
      parents.forEach((parent) => {
        const parentEmail = parent.parentEmail || parent.email || parent.id || '';
        const isParentPro = parent.isPro === true;

        const names: string[] = Array.isArray(parent.studentNames)
          ? parent.studentNames.filter(Boolean)
          : typeof parent.studentNames === 'string' && parent.studentNames
            ? [parent.studentNames]
            : [];

        if (names.length === 0) return;

        names.forEach((name, idx) => {
          const studentVirtualId = `${parentEmail}__${idx}__${name}`;

          const matchingStudent = students.find((s: any) => {
            const sName = (s.name || s.studentName || '').trim();
            const sEmail = (s.parentEmail || '').trim().toLowerCase();
            const targetEmail = parentEmail.trim().toLowerCase();
            const targetName = name.trim();
            if (sEmail && targetEmail) {
              return sName === targetName && sEmail === targetEmail;
            }
            return sName === targetName;
          });

          const studentClass =
            matchingStudent?.studentClass ||
            matchingStudent?.className ||
            '-';

          let studentBus: string = '-';
          if (matchingStudent?.assignedBusNumber) {
            studentBus = String(matchingStudent.assignedBusNumber);
          } else if (
            Array.isArray(matchingStudent?.searchTags) &&
            matchingStudent.searchTags.length > 0
          ) {
            const firstTag = String(matchingStudent.searchTags[0]);
            const busMatch = firstTag.match(/BUS[_-]?(\w+)/i);
            studentBus = busMatch ? busMatch[1] : firstTag;
          }

          list.push({
            id: `virtual_${studentVirtualId}_${currentPeriod}`,
            studentId: studentVirtualId,
            studentName: name,
            studentClass,
            assignedBusNumber: studentBus,
            period: currentPeriod,
            amount: PRO_ANNUAL_PRICE_EUR,
            status: isParentPro ? 'paid' : 'pending',
            isPro: isParentPro,
            parentEmail,
            parentDocId: parent.id,
          });
        });
      });
    } else if (students.length > 0) {
      students.forEach((student) => {
        const name = student.name || student.studentName || '-';
        list.push({
          id: `virtual_${student.id}_${currentPeriod}`,
          studentId: student.id,
          studentName: name,
          studentClass: student.studentClass || student.className || '-',
          assignedBusNumber: student.assignedBusNumber || '-',
          period: currentPeriod,
          amount: PRO_ANNUAL_PRICE_EUR,
          status: student.isPro ? 'paid' : 'pending',
          isPro: student.isPro === true,
        });
      });
    }

    return list;
  }, [parents, students, currentPeriod]);

  const uniqueClasses = useMemo(() => {
    const classes = combinedPayments
      .map((p) => p.studentClass)
      .filter((c): c is string => Boolean(c && c !== '-'));
    return Array.from(new Set(classes)).sort();
  }, [combinedPayments]);

  const filteredPayments = useMemo(() => {
    return combinedPayments.filter((p) => {
      const matchSearch =
        searchTerm === '' ||
        p.studentName.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (p.parentEmail &&
          p.parentEmail.toLowerCase().includes(searchTerm.toLowerCase()));

      const matchStatus = filterStatus === 'all' || p.status === filterStatus;
      const matchClass = filterClass === 'all' || p.studentClass === filterClass;

      return matchSearch && matchStatus && matchClass;
    });
  }, [combinedPayments, searchTerm, filterStatus, filterClass]);

  const offerablePayments = useMemo(
    () => filteredPayments.filter((p) => !p.isPro),
    [filteredPayments]
  );

  const selectedPayments = useMemo(
    () => filteredPayments.filter((p) => selectedIds.has(p.id)),
    [filteredPayments, selectedIds]
  );

  const allSelected =
    offerablePayments.length > 0 &&
    offerablePayments.every((p) => selectedIds.has(p.id));

  const someSelected =
    offerablePayments.some((p) => selectedIds.has(p.id)) && !allSelected;

  const stats = useMemo(() => {
    const proCount = filteredPayments.filter((p) => p.isPro).length;
    const nonProCount = filteredPayments.filter((p) => !p.isPro).length;
    return {
      proCount,
      nonProCount,
      totalCollected: proCount * PRO_ANNUAL_PRICE_EUR,
      selectionTotal: selectedPayments.length * PRO_ANNUAL_PRICE_EUR,
    };
  }, [filteredPayments, selectedPayments]);

  const toggleOne = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    setSelectedIds((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        offerablePayments.forEach((p) => next.delete(p.id));
        return next;
      } else {
        const next = new Set(prev);
        offerablePayments.forEach((p) => next.add(p.id));
        return next;
      }
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const getStatusBadge = (payment: PaymentRecord) => {
    if (payment.isPro) {
      return (
        <span style={styles.badgePro}>
          ⚡ {t('pro_annual', 'PRO Annuel')}
        </span>
      );
    }
    return (
      <span style={styles.badgeWarning}>
        ⏳ {t('pending', 'En attente')}
      </span>
    );
  };

  const textAlignStyle: CSSProperties = {
    textAlign: isRTL ? 'right' : 'left',
  };

  const formatPrice = (amount: number) =>
    `${amount.toFixed(2).replace('.', ',')} ${CURRENCY_SYMBOL}`;

  const handleCheckout = () => {
    if (selectedPayments.length === 0) return;
    setShowConfirmModal(true);
  };

  const confirmCheckout = () => {
    setShowConfirmModal(false);
    if (onCheckout) {
      const totalAmountInCents = selectedPayments.length * PRO_ANNUAL_PRICE_CENTS;
      onCheckout(selectedPayments, totalAmountInCents);
    }
  };

  return (
    <div style={{ ...styles.container, direction: isRTL ? 'rtl' : 'ltr' }}>
      {/* HEADER */}
      <div style={styles.headerContainer}>
        <div style={textAlignStyle}>
          <h2 style={styles.title}>
            💳 {t('payment_sheet_title', 'Abonnements PRO Annuels')}
          </h2>
          <p style={styles.subtitle}>
            {t('payment_sheet_subtitle', 'Abonnement PRO : {{price}} / an', {
              price: formatPrice(PRO_ANNUAL_PRICE_EUR),
            })}
          </p>
        </div>

        <div style={styles.statsContainer}>
          <div style={styles.statCardPro}>
            <span style={styles.statLabel}>
              ⚡ {t('pro_count', 'Abonnés PRO')}
            </span>
            <span style={styles.statValue}>{stats.proCount}</span>
          </div>

          <div style={styles.statCardSuccess}>
            <span style={styles.statLabel}>
              {t('total_collected', 'Encaissé')}
            </span>
            <span style={styles.statValue}>
              {formatPrice(stats.totalCollected)}
            </span>
          </div>

          <div
            style={
              selectedPayments.length > 0
                ? styles.statCardSelectionActive
                : styles.statCardSelection
            }
          >
            <span style={styles.statLabel}>
              {selectedPayments.length > 0
                ? t('selection_total', 'Montant de la sélection')
                : t('select_to_see_total', 'Sélectionnez des élèves')}
            </span>
            <span style={styles.statValue}>
              {selectedPayments.length > 0
                ? formatPrice(stats.selectionTotal)
                : '—'}
            </span>
          </div>
        </div>
      </div>

      {/* BANDEAU FLEXIBILITÉ */}
      <div style={styles.flexBanner}>
        <span style={{ fontSize: '22px' }}>💡</span>
        <div style={{ flex: 1, ...textAlignStyle }}>
          <div style={styles.flexTitle}>
            {t('flexibility_title', 'Flexibilité pour les établissements')}
          </div>
          <div style={styles.flexDesc}>
            {t(
              'flexibility_desc',
              "Sélectionnez les élèves que vous souhaitez passer en PRO. Le montant total s'affiche en haut. Le paiement se fait par carte bancaire."
            )}
          </div>
        </div>
      </div>

      {/* FILTRES */}
      <div style={styles.filterBar}>
        <input
          type="text"
          placeholder={`🔍 ${t(
            'search_student_or_receipt',
            'Rechercher élève ou email...'
          )}`}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />

        {uniqueClasses.length > 0 && (
          <select
            value={filterClass}
            onChange={(e) => setFilterClass(e.target.value)}
            style={styles.select}
          >
            <option value="all">
              🎓 {t('all_classes', 'Toutes les classes')}
            </option>
            {uniqueClasses.map((cls) => (
              <option key={cls} value={cls}>
                {cls}
              </option>
            ))}
          </select>
        )}

        <select
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          style={styles.select}
        >
          <option value="all">
            📌 {t('all_statuses', 'Tous les statuts')}
          </option>
          <option value="paid">{t('pro_active', 'PRO actif')}</option>
          <option value="pending">{t('pending', 'En attente')}</option>
        </select>
      </div>

      {/* BARRE D'ACTIONS GROUPÉES */}
      {selectedPayments.length > 0 && (
        <div style={styles.bulkBar}>
          <div
            style={{ ...textAlignStyle, fontWeight: 'bold', color: '#1e293b' }}
          >
            ✅ {t('selected_count', '{{count}} sélectionné(s)', {
              count: selectedPayments.length,
            })}{' '}
            — {formatPrice(stats.selectionTotal)}
          </div>
          <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
            <button onClick={handleCheckout} style={styles.paySelectionBtn}>
              💳{' '}
              {t('pay_amount_card', 'Payer {{amount}} par carte', {
                amount: formatPrice(stats.selectionTotal),
              })}
            </button>
            <button onClick={clearSelection} style={styles.cancelBtn}>
              ✖ {t('cancel', 'Annuler')}
            </button>
          </div>
        </div>
      )}

      {/* TABLEAU */}
      <div style={styles.tableWrapper}>
        <table style={{ ...styles.table, ...textAlignStyle }}>
          <thead>
            <tr style={styles.thRow}>
              <th style={{ ...styles.th, ...textAlignStyle, width: '40px' }}>
                <input
                  type="checkbox"
                  checked={allSelected}
                  ref={(el) => {
                    if (el) el.indeterminate = someSelected;
                  }}
                  onChange={toggleAll}
                  style={styles.checkbox}
                  title={t('select_all', 'Tout sélectionner')}
                />
              </th>
              <th style={{ ...styles.th, ...textAlignStyle }}>
                {t('col_student', 'ÉLÈVE')}
              </th>
              <th style={{ ...styles.th, ...textAlignStyle }}>
                {t('col_class', 'CLASSE')}
              </th>
              <th style={{ ...styles.th, ...textAlignStyle }}>
                {t('col_parent', 'PARENT')}
              </th>
              <th style={{ ...styles.th, ...textAlignStyle }}>
                {t('col_period', 'PÉRIODE')}
              </th>
              <th style={{ ...styles.th, ...textAlignStyle }}>
                {t('col_status', 'STATUT')}
              </th>
              <th style={{ ...styles.th, ...textAlignStyle }}>
                {t('col_actions', 'ACTIONS')}
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredPayments.length > 0 ? (
              filteredPayments.map((payment) => {
                const isSelectable = !payment.isPro;
                const isSelected = selectedIds.has(payment.id);

                return (
                  <tr
                    key={payment.id}
                    style={{
                      ...styles.tr,
                      background: isSelected ? '#eff6ff' : 'transparent',
                    }}
                  >
                    <td style={{ ...styles.td, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={!isSelectable}
                        onChange={() => toggleOne(payment.id)}
                        style={{
                          ...styles.checkbox,
                          cursor: isSelectable ? 'pointer' : 'not-allowed',
                          opacity: isSelectable ? 1 : 0.3,
                        }}
                      />
                    </td>
                    <td style={{ ...styles.td, ...textAlignStyle }}>
                      <div style={{ fontWeight: 'bold', color: '#2d3748' }}>
                        {payment.studentName}
                      </div>
                    </td>
                    <td style={{ ...styles.td, ...textAlignStyle }}>
                      {payment.studentClass && payment.studentClass !== '-' ? (
                        <span style={styles.classBadge}>
                          {payment.studentClass}
                        </span>
                      ) : (
                        <span style={{ color: '#cbd5e0' }}>—</span>
                      )}
                    </td>
                    <td style={{ ...styles.td, ...textAlignStyle }}>
                      <div style={{ fontSize: '0.85rem', color: '#4a5568' }}>
                        {payment.parentEmail || '-'}
                      </div>
                    </td>
                    <td style={{ ...styles.td, ...textAlignStyle }}>
                      <strong>{payment.period}</strong>
                    </td>
                    <td style={{ ...styles.td, ...textAlignStyle }}>
                      {getStatusBadge(payment)}
                    </td>
                    <td style={{ ...styles.td, ...textAlignStyle }}>
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        {payment.isPro && onDownloadReceipt && (
                          <button
                            onClick={() => onDownloadReceipt(payment)}
                            style={styles.receiptBtn}
                            title={t(
                              'download_receipt',
                              'Télécharger justificatif'
                            )}
                          >
                            📄 {t('btn_receipt', 'Justif.')}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={7} style={styles.noData}>
                  🚫 {t('no_payments_found', 'Aucun élève trouvé.')}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* MODALE DE CONFIRMATION */}
      {showConfirmModal && (
        <div style={styles.modalOverlay} onClick={() => setShowConfirmModal(false)}>
          <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: '3rem', marginBottom: '15px' }}>💳</div>
            <h3
              style={{
                margin: '0 0 10px 0',
                color: '#1a202c',
                fontSize: '1.3rem',
                fontWeight: 900,
              }}
            >
              {t('confirm_checkout_title', 'Confirm payment')}
            </h3>
            <p
              style={{
                color: '#718096',
                marginBottom: '25px',
                fontSize: '1rem',
                lineHeight: 1.5,
              }}
            >
              {t(
                'confirm_checkout_msg',
                'Do you want to pay {{amount}} for {{count}} student(s)?',
                {
                  amount: formatPrice(stats.selectionTotal),
                  count: selectedPayments.length,
                }
              )}
            </p>
            <div
              style={{
                display: 'flex',
                gap: '10px',
                justifyContent: 'center',
              }}
            >
              <button
                onClick={() => setShowConfirmModal(false)}
                style={styles.modalCancelBtn}
              >
                ✖ {t('cancel', 'Cancel')}
              </button>
              <button onClick={confirmCheckout} style={styles.modalPayBtn}>
                💳 {t('pay_now', 'Pay now')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const styles: { [key: string]: CSSProperties } = {
  container: { background: '#ffffff', borderRadius: '16px', padding: '20px', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.05)' },
  headerContainer: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px', marginBottom: '20px' },
  title: { margin: 0, fontSize: '1.4rem', fontWeight: 900, color: '#1a202c' },
  subtitle: { margin: '4px 0 0', fontSize: '0.85rem', color: '#718096' },
  statsContainer: { display: 'flex', gap: '12px', flexWrap: 'wrap' },
  statCardPro: { background: '#eff6ff', border: '1px solid #bfdbfe', padding: '10px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column' },
  statCardSuccess: { background: '#f0fdf4', border: '1px solid #bbf7d0', padding: '10px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column' },
  statCardSelection: { background: '#f8fafc', border: '1px dashed #cbd5e0', padding: '10px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', minWidth: '150px' },
  statCardSelectionActive: { background: '#fef3c7', border: '1px solid #fcd34d', padding: '10px 16px', borderRadius: '12px', display: 'flex', flexDirection: 'column', minWidth: '150px' },
  statLabel: { fontSize: '0.75rem', fontWeight: 'bold', color: '#64748b' },
  statValue: { fontSize: '1.1rem', fontWeight: 900, color: '#0f172a' },

  flexBanner: { display: 'flex', gap: '12px', alignItems: 'center', background: 'linear-gradient(135deg,#eff6ff,#f0f9ff)', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '14px 16px', marginBottom: '16px' },
  flexTitle: { fontWeight: 800, color: '#1e40af', fontSize: '1.15rem', marginBottom: '2px' },
  flexDesc: { fontSize: '1.10rem', color: '#1d4ed8', lineHeight: 1.55 },

  filterBar: { display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '16px', background: '#f8fafc', padding: '12px', borderRadius: '12px', border: '1px solid #e2e8f0', alignItems: 'center' },
  searchInput: { flex: '1 1 200px', padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e0', outline: 'none', fontSize: '0.9rem' },
  select: { padding: '8px 12px', borderRadius: '8px', border: '1px solid #cbd5e0', background: '#ffffff', fontWeight: 'bold', fontSize: '0.85rem', cursor: 'pointer', outline: 'none' },

  bulkBar: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '12px', flexWrap: 'wrap', background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '12px', padding: '12px 16px', marginBottom: '16px' },
  paySelectionBtn: { background: '#10b981', color: 'white', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' },
  cancelBtn: { background: '#e2e8f0', color: '#475569', border: 'none', padding: '10px 18px', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.85rem' },

  tableWrapper: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse' },
  thRow: { background: '#f1f5f9' },
  th: { padding: '12px', fontSize: '0.8rem', fontWeight: 800, color: '#475569', textTransform: 'uppercase' },
  tr: { borderBottom: '1px solid #f1f5f9', transition: 'background 0.15s' },
  td: { padding: '12px', fontSize: '0.9rem', color: '#334155' },
  checkbox: { width: '18px', height: '18px', cursor: 'pointer', accentColor: '#10b981' },

  classBadge: { background: '#f1f5f9', color: '#475569', padding: '3px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-block' },
  badgePro: { background: 'linear-gradient(135deg,#3b82f6,#8b5cf6)', color: 'white', padding: '4px 10px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold', display: 'inline-block' },
  badgeWarning: { background: '#fef9c3', color: '#854d0e', padding: '4px 8px', borderRadius: '6px', fontSize: '0.75rem', fontWeight: 'bold' },

  payProBtn: { background: '#3b82f6', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' },
  receiptBtn: { background: '#10b981', color: 'white', border: 'none', padding: '6px 12px', borderRadius: '6px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.75rem' },
  noData: { textAlign: 'center', padding: '30px', color: '#94a3b8', fontWeight: 'bold' },

  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.6)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
  },
  modalCard: {
    backgroundColor: 'white',
    padding: '35px 30px',
    borderRadius: '16px',
    maxWidth: '420px',
    width: '90%',
    textAlign: 'center',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
  },
  modalCancelBtn: {
    padding: '12px 25px',
    background: '#e2e8f0',
    color: '#475569',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '0.95rem',
    flex: 1,
  },
  modalPayBtn: {
    padding: '12px 25px',
    background: '#10b981',
    color: 'white',
    border: 'none',
    borderRadius: '10px',
    fontWeight: 'bold',
    cursor: 'pointer',
    fontSize: '0.95rem',
    flex: 1,
  },
};